import { useCallback, useEffect, useState } from 'react'
import { summariseHome, type HomeSummary } from '@core/home'
import { loadLetters } from '@core/letters'
import { formatCents } from '@core/money'
import { loadPayables, remainingCents, type Payable } from '@core/payments'
import type { FamilySession } from '@core/session'
import { Card, Screen, Section, Spinner } from '@design/components'
import { Tags } from '@design/Tags'
import { IncomingSection } from '@features/incoming/IncomingSection'
import { CollectionPrompt } from '@features/payments/CollectionPrompt'
import { SharedSpaces } from '@features/expenses/SharedSpaces'
import { PaymentCard } from '@features/payments/PaymentCard'
import { Totals } from './Totals'

/**
 * Home — what needs you, and what is coming.
 *
 * Every section hides when empty (rule 23), so on a quiet day this collapses
 * to a single line saying so. That is the point: an app that can honestly tell
 * you nothing needs doing is one you keep trusting on the days it says
 * something does.
 */
export function HomeScreen({ session }: { session: FamilySession }) {
  const [summary, setSummary] = useState<HomeSummary | null>(null)
  const [incoming, setIncoming] = useState(0)
  const [open, setOpen] = useState<Payable | null>(null)

  const refresh = useCallback(() => {
    Promise.all([loadPayables().catch(() => []), loadLetters().catch(() => [])])
      .then(([payables, tasks]) => setSummary(summariseHome(payables, tasks)))
      .catch(() => setSummary(null))
  }, [])

  useEffect(refresh, [refresh])

  return (
    <Screen>
      <IncomingSection userId={session.userId} spaces={session.spaces} onCount={setIncoming} />

      {!summary && <Spinner label="Even geduld…" />}

      {/* Above everything else it could be confused with: a direct debit that
          failed looks like nothing at all until someone is asked. */}
      <CollectionPrompt userId={session.userId} />

      {summary && (
        <>
          {summary.urgent.length > 0 && (
            <Section title="Vraagt aandacht" count={summary.urgent.length}>
              <div className="space-y-2">
                {summary.urgent.map((item) => (
                  <button
                    key={item.instalmentId}
                    onClick={() => setOpen(item)}
                    className="w-full text-left"
                  >
                    <Card className="flex items-center gap-3 border-warn/40">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">
                          {item.supplierName ?? item.description}
                        </p>
                        <p className="text-sm text-warn">{lateness(item)}</p>
                        <Tags
                          spaceName={item.spaceName}
                          spaceKind={item.spaceKind}
                          category={item.category}
                        />
                      </div>
                      <span className="shrink-0 tabular-nums font-medium">
                        {formatCents(remainingCents(item))}
                      </span>
                    </Card>
                  </button>
                ))}
              </div>
            </Section>
          )}

          {summary.needsYou.length > 0 && (
            <Section title="Brieven" count={summary.needsYou.length}>
              <div className="space-y-2">
                {summary.needsYou.slice(0, 4).map((task) => (
                  <Card key={task.id}>
                    <p className="font-medium truncate">{task.title}</p>
                    {task.detail && <p className="text-sm text-muted truncate">{task.detail}</p>}
                  </Card>
                ))}
              </div>
            </Section>
          )}

          <Totals windows={summary.windows} />

          {summary.waitingOn.length > 0 && (
            <Section title="Wachten op anderen" count={summary.waitingOn.length}>
              <div className="space-y-2">
                {summary.waitingOn.map((task) => (
                  <Card key={task.id} className="opacity-70">
                    <p className="font-medium truncate">{task.title}</p>
                    <p className="text-sm text-muted">Wacht op {task.waitingOn}</p>
                  </Card>
                ))}
              </div>
            </Section>
          )}

          {summary.clear && incoming === 0 && (
            <Card className="text-center py-8">
              <p className="text-lg font-medium mb-1">Alles is in orde</p>
              <p className="text-muted text-sm">Er is niets dat op jou wacht.</p>
            </Card>
          )}
        </>
      )}

      <SharedSpaces canEdit userId={session.userId} />

      {open && (
        <PaymentCard
          payable={open}
          userId={session.userId}
          onClose={() => setOpen(null)}
          onPaid={() => {
            setOpen(null)
            refresh()
          }}
        />
      )}
    </Screen>
  )
}

/** "3 dagen te laat" says more than a date does. */
function lateness(payable: Payable): string {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const due = new Date(`${payable.dueDate}T00:00:00`)
  const days = Math.round((today.getTime() - due.getTime()) / 86400000)

  if (payable.disputed) return 'Betwist'
  if (days > 1) return `${days} dagen te laat`
  if (days === 1) return 'Sinds gisteren te laat'
  if (days === 0) return 'Vandaag'
  return 'Vraagt aandacht'
}
